import Guard from 'arg-guard'

import Logger from '@/logger'

import BuildingPart from './building-part'

/**
 * Defined structure of a segment.
 *
 * @export Segment
 * @class Segment
 */
export default class Segment {

    /**
     * Creates an instance of a Segment.
     * @param {Object} data Source data - From API or state.
     * @memberof Segment
     */
    constructor(data) {
        Guard.throwIf(data, 'data')

        this.$key = data.key

        const tempConfidence = data.confidence ? parseInt(data.confidence) : 0
        if (tempConfidence < 2) {
            this.$confidence = 0
            this.confidence = 'No data / Extrapolated'
        } else if (tempConfidence < 4) {
            this.$confidence = 1
            this.confidence = 'Some uncertain data'
        } else if (tempConfidence < 6) {
            this.$confidence = 2
            this.confidence = 'Fair or old data'
        } else if (tempConfidence < 9) {
            this.$confidence = 3
            this.confidence = 'Good data'
        } else {
            this.$confidence = 4
            this.confidence = 'Excellent data'
        }

        this.data = data.items ? data.items : []
        Logger.logObject(this, 'Segment created', 'MyHome')
    }

    /**
     * Gets the building parts for the segment.
     *
     * @returns {Array} A collection of building parts.
     * @memberof Segment
     */
    buildingParts() {
        return this.data ? Array.from(new Set(this.data
            .filter(ei => ei.partIndex).map(ei => ei.partIndex)))
            .map(partIndex => {
                return new BuildingPart(partIndex, this.data.filter(ei => ei.partIndex === partIndex))
            }) : []
    }

    /**
     * Gets root segment data (no building parts).
     *
     * @returns {Array}
     * @memberof Segment
     */
    rootData() {
        return this.data ? this.data.filter(item => !item.partIndex) : []
    }

    /**
     * Gets segment data for a specific building part.
     *
     * @param {number} partIndex
     * @returns {Array}
     * @memberof Segment
     */
    partData(partIndex) {
        return this.data ? this.data.filter(item => item.partIndex === partIndex) : []
    }

    /**
     * Sets the value for an edited item.
     *
     * @param {string} dottedString
     * @param {Any} value
     * @returns {boolean} A flag to indicate if any data was changed
     * @memberof Segment
     */
    setDataItemValue(dottedString, value) {
        const ref = this.data.find(item => item.dottedString === dottedString)
        if (ref) {
            let changed = false
            if (ref.value !== value) {
                ref.value = value
                changed = true
            }
            return changed
        }

        throw new Error(`Unable to set data item value '${ dottedString }'`)
    }

    /**
     * Determines if a given value has changed in the segment.
     *
     * @param {string} dottedString The property to test
     * @param {Any} newValue The value to test
     * @returns {boolean} A flag to indicate if the value has changed
     * @memberof Segment
     */
    valueHasChanged(dottedString, newValue) {
        const ref = this.data.find(item => item.dottedString === dottedString)
        return !ref || ref.value !== newValue
    }

    /**
     * Ensures that the provided data is already of this type. If not, it runs the constructor.
     *
     * @static
     * @param {Object} data Source data.
     * @returns {Segment} Either the data is passed back if already of this type or the result of the constructor is.
     * @memberof Segment
     */
    static ensure(data) {
        return data && data instanceof Segment ? data : new Segment(data)
    }
}
